import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    draftUrl: String,
    resumeToken: String,
    sampleUrl: String
  }

  clear(event) {
    event.preventDefault()
    this.element.reset()
  }

  print(event) {
    event.preventDefault()
    window.print()
  }

  downloadJson(event) {
    event.preventDefault()
    const data = this.serializeForm()
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download = `loan_application_${this.timestamp()}.json`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(link.href)
  }

  async saveDraft(event) {
    event.preventDefault()
    if (!this.hasDraftUrlValue) return

    const payload = this.serializeForm()
    const resumeToken = this.hasResumeTokenValue ? this.resumeTokenValue : null

    try {
      const response = await fetch(this.draftUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({ loan_application: payload, resume_token: resumeToken })
      })

      if (response.ok) {
        const data = await response.json()
        this.resumeTokenValue = data.resume_token
        this.dispatch("saved", { detail: data })
      } else {
        const error = await response.json().catch(() => ({}))
        this.dispatch("error", { detail: error })
      }
    } catch (error) {
      this.dispatch("error", { detail: { message: error.message } })
    }
  }

  async populateSample(event) {
    event.preventDefault()
    if (!this.hasSampleUrlValue) return

    try {
      const response = await fetch(this.sampleUrlValue, {
        headers: { Accept: "application/json" }
      })

      if (!response.ok) {
        throw new Error(`Sample request failed (${response.status})`)
      }

      const payload = await response.json()
      const data = payload.loan_application || payload
      this.applySample(data)
      this.dispatch("sampled", { detail: data })
    } catch (error) {
      this.dispatch("error", { detail: { message: error.message } })
    }
  }

  serializeForm() {
    const formData = new FormData(this.element)
    const result = {}

    for (const [key, value] of formData.entries()) {
      const match = key.match(/^loan_application\[(.+)\]$/)
      if (!match) continue

      this.assignNested(result, match[1], value)
    }

    return result
  }

  assignNested(target, keyPath, value) {
    const parts = keyPath.replace(/\]/g, "").split("[")
    let current = target

    parts.forEach((part, index) => {
      const last = index === parts.length - 1
      if (last) {
        current[part] = value
      } else {
        current[part] ||= {}
        current = current[part]
      }
    })
  }

  applySample(data, path = []) {
    Object.entries(data).forEach(([key, value]) => {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        this.applySample(value, [...path, key])
      } else {
        this.setField([...path, key], value)
      }
    })
  }

  setField(pathParts, value) {
    const name = pathParts.reduce(
      (memo, part) => `${memo}[${part}]`,
      "loan_application"
    )

    const elements = this.element.querySelectorAll(`[name="${name}"]`)
    if (!elements.length) return

    const normalized = value ?? ""

    elements.forEach((element) => {
      if (element.type === "checkbox") {
        element.checked = Boolean(normalized)
      } else if (element.type === "hidden" && elements.length > 1 && elements[1].type === "checkbox") {
        element.value = normalized ? "1" : "0"
      } else {
        element.value = normalized
      }

      element.dispatchEvent(new Event("input", { bubbles: true }))
      element.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.getAttribute("content") : ""
  }

  timestamp() {
    return new Date().toISOString().slice(0, 10)
  }
}
